/**
 * Utility functions for calculating breakdown metrics across different dimensions
 * (Time Patterns, Performance)
 * 
 * Note: Asset aggregation is handled by the backend API endpoint
 */

import { DailyProfitSummary, HourlyProfitBreakdown, AssetBreakdownSummary } from '../api/profit';

// ============================================================================
// Type Definitions
// ============================================================================

// Re-export AssetBreakdownSummary from API for convenience
export type AssetBreakdown = AssetBreakdownSummary;

export interface DayOfWeekBreakdown {
  day_of_week: number; // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  day_name: string;
  net_profit: number;
  gross_profit: number;
  trade_count: number;
  profitable_days: number;
  total_days: number;
  win_rate: number;
  percent_of_total: number;
}

export interface HourOfDayBreakdown {
  hour: number; // 0-23
  net_profit: number;
  gross_profit: number;
  trade_count: number;
  percent_of_total: number;
}

export interface ProfitabilityMetrics {
  win_rate: number;
  avg_win: number;
  avg_loss: number;
  win_loss_ratio: number;
  profit_factor: number;
  best_day: { date: string; profit: number } | null;
  worst_day: { date: string; profit: number } | null;
  max_drawdown: number;
  total_profitable_days: number;
  total_trading_days: number;
}

export interface EfficiencyMetrics {
  avg_profit_per_trade: number;
  fee_ratio: number;
  cost_efficiency: number;
  trading_days: number;
  trades_per_day_avg: number;
  total_trades: number;
  total_fees: number;
}

// ============================================================================
// Time Pattern Functions
// ============================================================================

/**
 * Aggregates daily profit data by day of week
 */
export function aggregateByDayOfWeek(dailyData: DailyProfitSummary[]): DayOfWeekBreakdown[] {
  if (!dailyData || dailyData.length === 0) {
    return [];
  }

  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  
  // Group by day of week
  const dayMap = new Map<number, {
    net_profit: number;
    gross_profit: number;
    trade_count: number;
    profitable_days: number;
    total_days: number;
  }>();

  dailyData.forEach(day => {
    // Parse date in UTC to avoid timezone issues
    const date = new Date(day.trade_date + 'T00:00:00Z');
    const dayOfWeek = date.getUTCDay(); // 0 = Sunday, 6 = Saturday

    const existing = dayMap.get(dayOfWeek) || {
      net_profit: 0,
      gross_profit: 0,
      trade_count: 0,
      profitable_days: 0,
      total_days: 0,
    };

    existing.net_profit += day.net_profit;
    existing.gross_profit += day.gross_profit;
    existing.trade_count += day.trade_count;
    existing.total_days += 1;
    if (day.net_profit > 0) {
      existing.profitable_days += 1;
    }

    dayMap.set(dayOfWeek, existing);
  });

  // Calculate total net profit for percentage calculations
  const totalNetProfit = dailyData.reduce((sum, d) => sum + d.net_profit, 0);

  // Convert to array and calculate derived metrics
  const breakdowns: DayOfWeekBreakdown[] = Array.from(dayMap.entries()).map(([dayOfWeek, data]) => {
    const win_rate = data.total_days > 0 ? (data.profitable_days / data.total_days) * 100 : 0;
    const percent_of_total = totalNetProfit !== 0 ? (data.net_profit / totalNetProfit) * 100 : 0;

    return {
      day_of_week: dayOfWeek,
      day_name: dayNames[dayOfWeek],
      net_profit: data.net_profit,
      gross_profit: data.gross_profit,
      trade_count: data.trade_count,
      profitable_days: data.profitable_days,
      total_days: data.total_days,
      win_rate,
      percent_of_total,
    };
  });

  // Sort by day of week (chronological order: Mon-Sun)
  return breakdowns.sort((a, b) => {
    // Convert Sunday (0) to 7 for sorting to get Mon-Sun order
    const aDay = a.day_of_week === 0 ? 7 : a.day_of_week;
    const bDay = b.day_of_week === 0 ? 7 : b.day_of_week;
    return aDay - bDay;
  });
}

/**
 * Aggregates hourly profit data by hour of day (0-23)
 */
export function aggregateByHourOfDay(hourlyData: HourlyProfitBreakdown[]): HourOfDayBreakdown[] {
  if (!hourlyData || hourlyData.length === 0) {
    return [];
  }

  // Group by hour
  const hourMap = new Map<number, {
    net_profit: number;
    gross_profit: number;
    trade_count: number;
  }>();

  hourlyData.forEach(hour => {
    const existing = hourMap.get(hour.trade_hour) || {
      net_profit: 0,
      gross_profit: 0,
      trade_count: 0,
    };

    existing.net_profit += hour.net_profit;
    existing.gross_profit += hour.gross_profit;
    existing.trade_count += hour.trade_count;

    hourMap.set(hour.trade_hour, existing);
  });

  // Calculate total net profit for percentage calculations
  const totalNetProfit = hourlyData.reduce((sum, h) => sum + h.net_profit, 0);

  // Create complete 24-hour array (fill missing hours with zeros)
  const breakdowns: HourOfDayBreakdown[] = [];
  for (let hour = 0; hour < 24; hour++) {
    const data = hourMap.get(hour) || {
      net_profit: 0,
      gross_profit: 0,
      trade_count: 0,
    };

    const percent_of_total = totalNetProfit !== 0 ? (data.net_profit / totalNetProfit) * 100 : 0;

    breakdowns.push({
      hour,
      net_profit: data.net_profit,
      gross_profit: data.gross_profit,
      trade_count: data.trade_count,
      percent_of_total,
    });
  }

  return breakdowns;
}

/**
 * Identifies peak hours (top N most profitable hours)
 */
export function identifyPeakHours(hourlyBreakdown: HourOfDayBreakdown[], topN: number = 3): number[] {
  return [...hourlyBreakdown]
    .sort((a, b) => b.net_profit - a.net_profit)
    .slice(0, topN)
    .map(h => h.hour)
    .sort((a, b) => a - b);
}

// ============================================================================
// Performance Metrics Functions
// ============================================================================

/**
 * Calculates comprehensive profitability metrics
 */
export function calculateProfitabilityMetrics(dailyData: DailyProfitSummary[]): ProfitabilityMetrics {
  if (!dailyData || dailyData.length === 0) {
    return {
      win_rate: 0,
      avg_win: 0,
      avg_loss: 0,
      win_loss_ratio: 0,
      profit_factor: 0,
      best_day: null,
      worst_day: null,
      max_drawdown: 0,
      total_profitable_days: 0,
      total_trading_days: 0,
    };
  }

  const winningDays = dailyData.filter(d => d.net_profit > 0);
  const losingDays = dailyData.filter(d => d.net_profit < 0);
  const totalDays = dailyData.length;

  // Win Rate
  const win_rate = totalDays > 0 ? (winningDays.length / totalDays) * 100 : 0;

  // Average Win
  const avg_win = winningDays.length > 0
    ? winningDays.reduce((sum, d) => sum + d.net_profit, 0) / winningDays.length
    : 0;

  // Average Loss
  const avg_loss = losingDays.length > 0
    ? losingDays.reduce((sum, d) => sum + d.net_profit, 0) / losingDays.length
    : 0;

  // Win/Loss Ratio
  const win_loss_ratio = avg_loss !== 0 ? Math.abs(avg_win / avg_loss) : 0;

  // Profit Factor (using gross_profit for winners and losers)
  const gross_profit = winningDays.reduce((sum, d) => sum + d.gross_profit, 0);
  const gross_loss = Math.abs(losingDays.reduce((sum, d) => sum + d.gross_profit, 0));
  const profit_factor = gross_loss > 0 ? gross_profit / gross_loss : 0;

  // Best Day
  const bestDayData = dailyData.reduce((max, d) => d.net_profit > max.net_profit ? d : max, dailyData[0]);
  const best_day = bestDayData.net_profit > 0 ? {
    date: bestDayData.trade_date,
    profit: bestDayData.net_profit,
  } : null;

  // Worst Day
  const worstDayData = dailyData.reduce((min, d) => d.net_profit < min.net_profit ? d : min, dailyData[0]);
  const worst_day = worstDayData.net_profit < 0 ? {
    date: worstDayData.trade_date,
    profit: worstDayData.net_profit,
  } : null;

  // Max Drawdown
  const max_drawdown = calculateMaxDrawdown(dailyData);

  return {
    win_rate,
    avg_win,
    avg_loss,
    win_loss_ratio,
    profit_factor,
    best_day,
    worst_day,
    max_drawdown,
    total_profitable_days: winningDays.length,
    total_trading_days: totalDays,
  };
}

/**
 * Calculates maximum drawdown (largest peak-to-trough decline)
 * Uses efficient single-pass algorithm
 */
function calculateMaxDrawdown(dailyData: DailyProfitSummary[]): number {
  if (!dailyData || dailyData.length === 0) {
    return 0;
  }

  // Sort by date to ensure chronological order
  const sorted = [...dailyData].sort((a, b) => a.trade_date.localeCompare(b.trade_date));

  let cumulativeProfit = 0;
  let peak = 0;
  let maxDrawdown = 0;

  sorted.forEach(day => {
    cumulativeProfit += day.net_profit;

    // Update peak if we've reached a new high
    if (cumulativeProfit > peak) {
      peak = cumulativeProfit;
    }

    // Calculate drawdown from peak
    const drawdown = peak - cumulativeProfit;
    if (drawdown > maxDrawdown) {
      maxDrawdown = drawdown;
    }
  });

  return maxDrawdown;
}

/**
 * Calculates efficiency metrics
 */
export function calculateEfficiencyMetrics(dailyData: DailyProfitSummary[]): EfficiencyMetrics {
  if (!dailyData || dailyData.length === 0) {
    return {
      avg_profit_per_trade: 0,
      fee_ratio: 0,
      cost_efficiency: 0,
      trading_days: 0,
      trades_per_day_avg: 0,
      total_trades: 0,
      total_fees: 0,
    };
  }

  const total_net_profit = dailyData.reduce((sum, d) => sum + d.net_profit, 0);
  const total_gross_profit = dailyData.reduce((sum, d) => sum + d.gross_profit, 0);
  const total_trades = dailyData.reduce((sum, d) => sum + d.trade_count, 0);
  const total_fees = dailyData.reduce((sum, d) => sum + (d.fees_paid || 0), 0);
  const trading_days = dailyData.length;

  // Avg Profit/Trade
  const avg_profit_per_trade = total_trades > 0 ? total_net_profit / total_trades : 0;

  // Fee Ratio: fees as a percentage of gross (pre-fee) profit.
  // gross_profit is the pre-fee trading P&L (daily_profit_vw defines
  // net_profit = gross_profit - fees), so fees / gross_profit is the share of
  // generated profit consumed by fees.
  const fee_ratio = total_gross_profit > 0 ? (total_fees / total_gross_profit) * 100 : 0;

  // Cost Efficiency (net profit per dollar of fees)
  const cost_efficiency = total_fees > 0 ? total_net_profit / total_fees : 0;

  // Trades/Day Average
  const trades_per_day_avg = trading_days > 0 ? total_trades / trading_days : 0;

  return {
    avg_profit_per_trade,
    fee_ratio,
    cost_efficiency,
    trading_days,
    trades_per_day_avg,
    total_trades,
    total_fees,
  };
}

// ============================================================================
// Benchmark Functions
// ============================================================================

export type BenchmarkLevel = 'excellent' | 'good' | 'warning' | 'poor';

/**
 * Get benchmark level for win rate
 */
export function getWinRateBenchmark(winRate: number): BenchmarkLevel {
  if (winRate >= 70) return 'excellent';
  if (winRate >= 50) return 'good';
  if (winRate >= 40) return 'warning';
  return 'poor';
}

/**
 * Get benchmark level for win/loss ratio
 */
export function getWinLossRatioBenchmark(ratio: number): BenchmarkLevel {
  if (ratio >= 3) return 'excellent';
  if (ratio >= 2) return 'good';
  if (ratio >= 1.5) return 'warning';
  return 'poor';
}

/**
 * Get benchmark level for profit factor
 */
export function getProfitFactorBenchmark(factor: number): BenchmarkLevel {
  if (factor >= 2.0) return 'excellent';
  if (factor >= 1.5) return 'good';
  if (factor >= 1.2) return 'warning';
  return 'poor';
}

/**
 * Get benchmark level for fee ratio
 */
export function getFeeRatioBenchmark(feeRatio: number): BenchmarkLevel {
  if (feeRatio < 3) return 'excellent';
  if (feeRatio < 5) return 'good';
  if (feeRatio < 7) return 'warning';
  return 'poor';
}

/**
 * Get icon/emoji for benchmark level
 */
export function getBenchmarkIcon(level: BenchmarkLevel): string {
  switch (level) {
    case 'excellent': return '⭐';
    case 'good': return '✅';
    case 'warning': return '⚠️';
    case 'poor': return '❌';
  }
}
